import { Fragment, ReactNode } from 'react'
import { useRouter } from 'next/router'
import { TFunction, useTranslation } from 'next-i18next'

import { Footer } from '~/components/Footer'
import { Link } from '~/components/Link'
import StarrySky from '~/assets/multgame/multgame-background-star-sky'
import { Breakpoint, useBreakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import ROUTES from '~/shared/constants/routes.const'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import SVGImage from '~/components/Image/SVGImage'

const mediaQuery = [{ screen: '(min-width: 280px)', width: '90vw' }]

export function SplitLayout({
  aside,
  fullAside,
  main,
  asideBg,
  logo,
  stretch,
}: {
  aside?: ReactNode
  fullAside?: ReactNode
  main: ReactNode
  asideBg?: string
  logo?: string
  stretch?: boolean
}): React.ReactElement {
  const router = useRouter()
  const { t } = useTranslation(['imageAlt'])
  const br = useBreakpoint()
  const isLoginOrRegisterPage = router.asPath.includes('login') || router.asPath.includes('register')
  const { bg, logoSrcKey, logoAlt, bgType } = getVariantConfig(router.query.from, isLoginOrRegisterPage, t)

  const shouldStretch = stretch ? 'my-auto flex-1' : ''

  const isStackNLoad = router.asPath.includes(ROUTES.STACKNLOAD)

  return (
    <>
      <div className="relative min-h-screen h-min flex flex-col lg:flex-row">
        <div className={`relative inset-0 lg:flex-1 lg:min-h-inherit ${bgType === 'image' || asideBg ? '' : bg}`}>
          {br >= Breakpoint.LG && <Background asideBg={asideBg} bg={bg} bgType={bgType} />}
          <Link to="/">
            <SVGImage
              alt={logo ? 'House of Math Logo' : logoAlt || 'House of Math Logo'}
              height={45}
              imageClassName={`${isStackNLoad ? 'ml-3' : 'mx-auto'} mt-3 lg:my-6 lg:mx-8`}
              srcKey={logo ? logo : logoSrcKey}
              width={234}
            />
          </Link>
          {fullAside ? (
            <div className="flex justify-center mt-4 w-full">{fullAside}</div>
          ) : (
            <>
              {aside && br >= Breakpoint.LG && <div className="block mr-5 w-full">{aside}</div>}
              <BeanStack isLoginOrRegisterPage={isLoginOrRegisterPage} />
            </>
          )}
        </div>
        <div
          className={
            'relative h-min py-4 lg:py-6 flex flex-col lg:items-center lg:my-auto lg:flex-1 ' + `${shouldStretch}`
          }
        >
          <div className="px-4 lg:px-8 w-full justify-center">
            {br < Breakpoint.LG && <div>{aside}</div>}
            <div className="w-full m-auto min-h-full lg:max-w-screen-sm mt-2 p-4 sm:p-16 lg:p-0 flex-1 flex items-center rounded shadow-experimental lg:shadow-none bg-white">
              {main}
            </div>
          </div>
        </div>
        {br < Breakpoint.LG && <Background asideBg={asideBg} bg={bg} bgType={bgType} />}
      </div>
      <Footer />
    </>
  )
}

const Background = ({
  asideBg,
  bgType,
  className,
  bg,
}: {
  asideBg?: string
  bgType?: string
  className?: string
  bg?: string
}): React.ReactElement => {
  const background =
    asideBg || (bgType && bgType === 'image') ? (
      <div
        className={`absolute inset-0 bg-cover bg-center -z-1 ${className}`}
        style={{ backgroundImage: `url(${asideBg || bg})` }}
      />
    ) : (
      <div className={`absolute inset-0 -z-1 ${bg}`} />
    )

  return background
}

const BeanStack = ({ isLoginOrRegisterPage = false }: { isLoginOrRegisterPage?: boolean }): React.ReactElement => {
  const { t } = useTranslation('imageAlt')
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.LG })

  if (!isDesktop) return <Fragment />

  return (
    <Fragment>
      {isLoginOrRegisterPage ? (
        <div className="3xl:h-screen flex 3xl:justify-center 3xl:items-center px-8 2xl:-ml-4 3xl:-ml-12 3xl:-mt-40 pointer-events-none">
          <ResponsiveImage
            alt={t('loginBeanstalk')}
            height={700}
            imageClassName="relative bottom-4"
            loading="eager"
            srcKey="backgrounds/bg-login-three.png"
            width={700}
          />
        </div>
      ) : (
        <div className="sm:w-4/5 h-auto absolute inset-0 overflow-hidden pointer-events-none">
          <ResponsiveImage
            alt={t('homBeanstalk')}
            height={905}
            imageClassName="absolute bottom-0"
            loading="eager"
            mediaQuery={mediaQuery}
            srcKey="bean-stalks/complete-profile-bean-stalk-v2.png"
            width={826}
          />
        </div>
      )}
    </Fragment>
  )
}

function getVariantConfig(
  variant: string | string[] | undefined,
  isLoginOrRegisterPage: boolean,
  t: TFunction,
): {
  bg: string
  bgType?: 'image' | 'solid'
  logoSrcKey: string
  logoAlt?: string
} {
  switch (variant) {
    case 'booking-for-self':
    case 'booking-for-other':
    case 'booking-video-chat':
    case 'tutor-on-demand':
      return {
        bg: 'bg-gradient-to-r from-purple to-[#58C1FC]',
        logoSrcKey: 'logos/house-of-math-purple-light.svg',
      }

    case 'multgame':
      return {
        bg: StarrySky,
        bgType: 'image',
        logoSrcKey: 'logos/house-of-math-white.svg',
      }

    default:
      return {
        bg: isLoginOrRegisterPage ? 'bg-white' : 'bg-blue-light',
        logoSrcKey: 'logos/house-of-math.svg',
        logoAlt: t('imageAlt:icons.houseOfMath'),
      }
  }
}
